// installable-rail-demo — exercises all three InstallableRail layout variants.
//
// Route: /installable-rail-demo
//
// Acceptance Criterion 7 (TASK#4567 / TASK#4562):
// "Backed by a storybook-style demo page that exercises all combos."
//
// Covers:
//   - layout: 'rail'  (horizontal scrolling chip row)
//   - layout: 'grid'  (responsive auto-fill card grid)
//   - layout: 'list'  (bordered vertical management list)
//   - domain: 'integration', 'library', 'mcp' variants
//   - InstallableModalStub mounted so every click produces a visible
//     placeholder modal rather than silently no-opping
//
// Uses mock data injected via children state so no network call is needed
// for the demo. The mock data bypasses the onInit fetch by pre-populating
// the root state keys that InstallableRail reads.
//
// Mount InstallableModalStub at the page root so every
// el.call('openInstallableModal', ...) click renders the Phase 3 stub.

const _MOCK_KINDS = [
  {
    kind: 'github',
    domain: 'integration',
    display: { icon: 'github', label: 'GitHub', tagline: 'Connect your repositories.' },
    enabled: true
  },
  {
    kind: 'slack',
    domain: 'integration',
    display: { icon: 'slack', label: 'Slack', tagline: 'Send notifications to channels.' },
    enabled: true
  },
  {
    kind: 'figma',
    domain: 'integration',
    display: { icon: 'figma', label: 'Figma', tagline: 'Import design tokens automatically.' },
    enabled: true
  },
  {
    kind: 'linear',
    domain: 'integration',
    display: { icon: 'linear', label: 'Linear', tagline: 'Sync issues and milestones.' },
    enabled: true
  },
  {
    kind: 'notion',
    domain: 'integration',
    display: { icon: 'notion', label: 'Notion', tagline: 'Publish docs from your workspace.' },
    enabled: true
  }
]

const _MOCK_INSTANCES = [
  { kind: 'github', scope_type: 'workspace', scope_id: 'demo', enabled: true },
  { kind: 'slack', scope_type: 'workspace', scope_id: 'demo', enabled: true }
]

const _MCP_MOCK_KINDS = [
  {
    kind: 'mcp-filesystem',
    domain: 'mcp',
    display: { icon: 'folder', label: 'Filesystem', tagline: 'Read and write local files via MCP.' },
    enabled: true
  },
  {
    kind: 'mcp-postgres',
    domain: 'mcp',
    display: { icon: 'database', label: 'Postgres', tagline: 'Query Postgres databases via MCP.' },
    enabled: true
  },
  {
    kind: 'mcp-github',
    domain: 'mcp',
    display: { icon: 'github', label: 'GitHub MCP', tagline: 'Manage repos and PRs via MCP.' },
    enabled: true
  }
]

export const installableRailDemo = {
  extends: 'Page',
  flow: 'y',
  gap: 'D',
  padding: 'C E',
  overflowY: 'auto',

  // Pre-populate root state keys so the rail renders without hitting the API.
  onInit: (el, s) => {
    const root = s.root
    root.update({
      // Integration rail (used by all three layout demos)
      '_installableRail_integration_workspace_kinds': _MOCK_KINDS,
      '_installableRail_integration_workspace_instances': _MOCK_INSTANCES,
      '_installableRail_integration_workspace_loading': false,
      // MCP rail
      '_installableRail_mcp_workspace_kinds': _MCP_MOCK_KINDS,
      '_installableRail_mcp_workspace_instances': [],
      '_installableRail_mcp_workspace_loading': false
    })
  },

  // Mount stub modal so clicks produce visible feedback (Phase 3).
  InstallableModalStub: {},

  // ── Page header ─────────────────────────────────────────────────────────

  DemoHeader: {
    flow: 'y',
    gap: 'Y',

    DemoTitle: {
      tag: 'h1',
      margin: '0',
      fontSize: 'E',
      fontWeight: '800',
      color: 'title',
      text: 'InstallableRail Demo'
    },

    DemoSubtitle: {
      tag: 'p',
      margin: '0',
      fontSize: 'Z2',
      color: 'caption',
      text: (
        'Exercises all three layout variants (rail / grid / list). ' +
        'Clicks open the Phase 3 InstallableModalStub stub modal — ' +
        'Phase 4 MarketplaceInstallModal will replace it.'
      )
    }
  },

  Divider_1: {
    borderTop: '1px',
    borderTopStyle: 'solid',
    borderTopColor: 'line'
  },

  // ── Section 1 — Rail layout ──────────────────────────────────────────────

  RailSection: {
    flow: 'y',
    gap: 'Z',

    SectionTitle: {
      tag: 'h2',
      margin: '0',
      fontSize: 'B',
      fontWeight: '700',
      color: 'title',
      text: 'layout: "rail"'
    },

    SectionHint: {
      tag: 'p',
      margin: '0',
      fontSize: 'Y2',
      color: 'caption',
      text: 'Horizontal scrolling chip row. Two items pre-installed (GitHub, Slack).'
    },

    InstallableRail: {
      domain: 'integration',
      scopeType: 'workspace',
      scopeId: 'demo',
      layout: 'rail',
      title: 'Integrations'
    }
  },

  Divider_2: {
    borderTop: '1px',
    borderTopStyle: 'solid',
    borderTopColor: 'line'
  },

  // ── Section 2 — Grid layout ──────────────────────────────────────────────

  GridSection: {
    flow: 'y',
    gap: 'Z',

    SectionTitle: {
      tag: 'h2',
      margin: '0',
      fontSize: 'B',
      fontWeight: '700',
      color: 'title',
      text: 'layout: "grid"'
    },

    SectionHint: {
      tag: 'p',
      margin: '0',
      fontSize: 'Y2',
      color: 'caption',
      text: 'Responsive auto-fill card grid. Same data source as the rail above.'
    },

    InstallableRail_grid: {
      extends: 'InstallableRail',
      domain: 'integration',
      scopeType: 'workspace',
      scopeId: 'demo',
      layout: 'grid',
      title: 'Integrations'
    }
  },

  Divider_3: {
    borderTop: '1px',
    borderTopStyle: 'solid',
    borderTopColor: 'line'
  },

  // ── Section 3 — List layout ──────────────────────────────────────────────

  ListSection: {
    flow: 'y',
    gap: 'Z',

    SectionTitle: {
      tag: 'h2',
      margin: '0',
      fontSize: 'B',
      fontWeight: '700',
      color: 'title',
      text: 'layout: "list"'
    },

    SectionHint: {
      tag: 'p',
      margin: '0',
      fontSize: 'Y2',
      color: 'caption',
      text: 'Bordered vertical management list with chevron + status badges.'
    },

    InstallableRail_list: {
      extends: 'InstallableRail',
      domain: 'integration',
      scopeType: 'workspace',
      scopeId: 'demo',
      layout: 'list',
      title: 'Integrations'
    }
  },

  Divider_4: {
    borderTop: '1px',
    borderTopStyle: 'solid',
    borderTopColor: 'line'
  },

  // ── Section 4 — MCP domain ───────────────────────────────────────────────

  McpSection: {
    flow: 'y',
    gap: 'Z',

    SectionTitle: {
      tag: 'h2',
      margin: '0',
      fontSize: 'B',
      fontWeight: '700',
      color: 'title',
      text: 'domain: "mcp" · layout: "grid"'
    },

    SectionHint: {
      tag: 'p',
      margin: '0',
      fontSize: 'Y2',
      color: 'caption',
      text: 'MCP connectors — none installed. Shows "Available" badge on all.'
    },

    InstallableRail_mcp: {
      extends: 'InstallableRail',
      domain: 'mcp',
      scopeType: 'workspace',
      scopeId: 'demo',
      layout: 'grid',
      title: 'MCP Connectors'
    }
  },

  Divider_5: {
    borderTop: '1px',
    borderTopStyle: 'solid',
    borderTopColor: 'line'
  },

  // ── Section 5 — Empty state ──────────────────────────────────────────────

  EmptySection: {
    flow: 'y',
    gap: 'Z',

    SectionTitle: {
      tag: 'h2',
      margin: '0',
      fontSize: 'B',
      fontWeight: '700',
      color: 'title',
      text: 'Empty state (no kinds)'
    },

    SectionHint: {
      tag: 'p',
      margin: '0',
      fontSize: 'Y2',
      color: 'caption',
      text: 'A "library" domain with no data pre-populated — shows InstallableRailEmpty.'
    },

    InstallableRail_empty: {
      extends: 'InstallableRail',
      domain: 'library',
      scopeType: 'workspace',
      scopeId: 'demo',
      layout: 'grid',
      title: 'Libraries',
      emptyHint: 'Add your first library'
    }
  }
}
